import React from 'react';
import QRCode from 'react-qr-code';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { Download, Share2, User } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import type { Ticket } from '@/types/tickets';
import { useUnifiedAuth } from '@/lib/auth/use-unified-auth';

interface QRCodeDisplayProps {
  ticket: Ticket;
  experienceTitle: string;
  experienceDate?: string;
  size?: number;
  showActions?: boolean;
}

export function QRCodeDisplay({ 
  ticket, 
  experienceTitle, 
  experienceDate,
  size = 200,
  showActions = true 
}: QRCodeDisplayProps) {
  const qrRef = React.useRef<HTMLDivElement>(null);
  const { user, profile } = useUnifiedAuth();
  
  // Show purchaser info by default, only show guest info if ticket has been claimed
  const ticketHolder = ticket.claimed_by && ticket.guest_name
    ? { name: ticket.guest_name, email: ticket.guest_email || 'Guest' }
    : { name: profile?.full_name || 'Ticket Holder', email: user?.email || '' };

  const downloadPDF = async () => {
    if (!qrRef.current) return;
    
    try {
      // Create a wrapper div for better PDF generation
      const wrapper = document.createElement('div');
      wrapper.style.padding = '40px';
      wrapper.style.backgroundColor = 'white';
      wrapper.style.display = 'inline-block';
      
      // Clone the QR code container
      const clone = qrRef.current.cloneNode(true) as HTMLDivElement;
      wrapper.appendChild(clone);
      document.body.appendChild(wrapper);
      
      // Generate canvas from the wrapper
      const canvas = await html2canvas(wrapper, {
        scale: 2,
        backgroundColor: '#ffffff'
      });
      
      // Remove wrapper
      document.body.removeChild(wrapper);
      
      // Create PDF
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });
      
      // Set background to white
      pdf.setFillColor(255, 255, 255);
      pdf.rect(0, 0, pdf.internal.pageSize.getWidth(), pdf.internal.pageSize.getHeight(), 'F');
      
      // Calculate dimensions to center the QR code
      const imgWidth = 150;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      const x = (pdf.internal.pageSize.getWidth() - imgWidth) / 2;
      const y = 40;
      
      // Add content to PDF - use black text for high contrast
      pdf.setTextColor(0, 0, 0); // Pure black
      pdf.setFontSize(24);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Rendezvous Ticket', pdf.internal.pageSize.getWidth() / 2, 20, { align: 'center' });
      
      // Add QR code image
      pdf.addImage(canvas.toDataURL('image/png'), 'PNG', x, y, imgWidth, imgHeight);
      
      // Add ticket details below QR code
      const detailsY = y + imgHeight + 15;
      
      // Ticket holder info
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(14);
      pdf.text(`Ticket Holder: ${ticketHolder.name}`, pdf.internal.pageSize.getWidth() / 2, detailsY, { align: 'center' });
      
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(12);
      pdf.text(ticketHolder.email, pdf.internal.pageSize.getWidth() / 2, detailsY + 7, { align: 'center' });
      
      // Allocation/Transfer status - use dark gray for secondary info
      if (ticket.transfer_type === 'allocation' && ticket.guest_name) {
        pdf.setTextColor(50, 50, 50); // Dark gray
        pdf.setFontSize(11);
        pdf.text('(Allocated ticket)', pdf.internal.pageSize.getWidth() / 2, detailsY + 15, { align: 'center' });
      } else if (ticket.transfer_type === 'transfer') {
        pdf.setTextColor(50, 50, 50); // Dark gray
        pdf.setFontSize(11);
        pdf.text('(Transferred ticket)', pdf.internal.pageSize.getWidth() / 2, detailsY + 15, { align: 'center' });
      }
      
      // Experience info
      pdf.setTextColor(0, 0, 0); // Back to black
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(18);
      pdf.text(experienceTitle, pdf.internal.pageSize.getWidth() / 2, detailsY + 28, { align: 'center' });
      
      if (experienceDate) {
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(12);
        pdf.text(experienceDate, pdf.internal.pageSize.getWidth() / 2, detailsY + 36, { align: 'center' });
      }
      
      // Footer text
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(11);
      pdf.text('Please present this QR code at check-in', pdf.internal.pageSize.getWidth() / 2, detailsY + 50, { align: 'center' });
      
      // Save PDF
      const fileName = `ticket-${experienceTitle.replace(/[^a-z0-9]/gi, '-').toLowerCase()}-${ticket.ticket_number}.pdf`;
      pdf.save(fileName);
    } catch (error) {
      console.error('Error generating PDF:', error);
    }
  };
  
  const shareTicket = async () => {
    const shareUrl = `${window.location.origin}/tickets/view/${ticket.qr_code}`;
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: `Ticket for ${experienceTitle}`,
          text: `Here's my ticket for ${experienceTitle}`,
          url: shareUrl
        });
      } catch (error) {
        // User cancelled or error
        console.error('Share failed:', error);
      }
    } else {
      // Fallback: copy to clipboard
      navigator.clipboard.writeText(shareUrl);
      alert('Link copied to clipboard!');
    }
  };

  return (
    <div className="bg-earth-50 p-6 rounded-lg">
      <div ref={qrRef} className="flex flex-col items-center">
        {/* Ticket Header - Updated to lighter earth tone */}
        <div className="bg-earth-100 text-earth-900 p-4 text-center w-full rounded-t-lg">
          <h3 className="text-xl font-bold">{experienceTitle}</h3>
          {experienceDate && (
            <p className="text-sm text-earth-700 mt-1">{experienceDate}</p>
          )}
        </div>

        {/* QR Code Container - Light background */}
        <div className="flex items-center justify-center py-8 bg-earth-50">
          <div className="bg-white p-4 rounded-lg">
            <QRCode 
              value={ticket.qr_code}
              size={size}
              level="H"
              style={{ height: "auto", maxWidth: "100%", width: "100%" }}
            />
          </div>
        </div>

        {/* Ticket Holder Info - Light earth tone background */}
        <div className="bg-earth-100 text-earth-900 p-4 text-center space-y-2 w-full rounded-b-lg">
          <div className="flex items-center justify-center">
            <User className="h-5 w-5 mr-2" />
            <span className="font-medium">{ticketHolder.name}</span>
          </div>
          <p className="text-sm text-earth-700">{ticketHolder.email}</p>
          
          {/* Show allocation/transfer status */}
          {ticket.transfer_type === 'allocation' && ticket.guest_name && (
            <p className="text-xs text-earth-600 mt-2">
              Allocated to: {ticket.guest_name}
            </p>
          )}
          {ticket.transfer_type === 'transfer' && (
            <p className="text-xs text-earth-600 mt-2">
              Transferred ticket
            </p>
          )}
        </div>
      </div>

      {/* Actions */}
      {showActions && (
        <div className="mt-6 flex gap-3">
          <Button
            onClick={downloadPDF}
            variant="secondary"
            className="flex-1 bg-earth-200 hover:bg-earth-300 text-earth-900"
          >
            <Download className="h-4 w-4 mr-2" />
            Download
          </Button>
          <Button
            onClick={shareTicket}
            variant="secondary"
            className="flex-1 bg-earth-200 hover:bg-earth-300 text-earth-900"
          >
            <Share2 className="h-4 w-4 mr-2" />
            Share
          </Button>
        </div>
      )}
    </div>
  );
} 